import React from 'react'
import { Helmet } from 'react-helmet-async'
import { i18n } from '@/locale/i18n'
import { useSSRUrl } from '@/contexts/SSRContext'
import { normalizeSeoPathname, toCanonicalUrl } from '@/util/seoCanonicalUrl'

const defaultOgImage =
  typeof import.meta !== 'undefined' && import.meta.env?.VITE_FUTUUR_STATIC_URL
    ? `${import.meta.env.VITE_FUTUUR_STATIC_URL}/img/meta/futuur.com.png`
    : ''

export default ({
  id,
  url,
  title,
  description,
  images,
  alt,
  follow = true,
  nofollow = false,
  omitCanonical = false,
  ...props
}) => {
  const ssrUrl = useSSRUrl()
  const rawImages = images != null ? images : (defaultOgImage ? [defaultOgImage] : [])
  const imageList = Array.isArray(rawImages) ? rawImages : [rawImages]

  const ldData = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: 'Futuur',
    url: 'https://futuur.com',
    sameAs: [
      'https://twitter.com/futuurHQ',
      'https://www.facebook.com/futuurHQ',
      'https://www.youtube.com/channel/UCEJkPOzzJzlV8jeb5mKRHeQ',
      'https://www.medium.com/futuur',
      'https://www.instagram.com/futuurHQ',
    ],
  }

  const pageUrl = url || (typeof window !== 'undefined' ? window.location.href : ssrUrl) || ''

  const canonicalUrl = toCanonicalUrl(pageUrl)
  // Keep Open Graph URL aligned with canonical (HTTPS, no query, slash policy).
  const ogUrl = canonicalUrl || pageUrl

  const buildLocaleAlternates = (canonical) => {
    try {
      const u = new URL(canonical)
      const path = u.pathname || '/'
      const isPtBr = path === '/pt-br' || path.startsWith('/pt-br/')
      const withoutLocale = isPtBr ? path.replace(/^\/pt-br(?=\/|$)/, '') || '/' : path
      const enUrl = new URL(u.toString())
      enUrl.pathname = normalizeSeoPathname(withoutLocale)
      const ptUrl = new URL(u.toString())
      const ptPath = `/pt-br${withoutLocale === '/' ? '' : withoutLocale}`
      ptUrl.pathname = normalizeSeoPathname(ptPath)
      return {
        xDefault: enUrl.toString(),
        en: enUrl.toString(),
        ptBr: ptUrl.toString(),
      }
    } catch (_) {
      return null
    }
  }

  const alternates = canonicalUrl ? buildLocaleAlternates(canonicalUrl) : null

  return (
    <Helmet prioritizeSeoTags={true} key={id}>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {follow ? (
        <meta name="robots" content="index, follow" />
      ) : (
        <meta name="robots" content={nofollow ? 'noindex, nofollow' : 'noindex, follow'} />
      )}
      {!omitCanonical && alternates && (
        <>
          <link rel="alternate" href={alternates.xDefault} hrefLang="x-default" />
          <link rel="alternate" href={alternates.en} hrefLang="en" />
          <link rel="alternate" href={alternates.ptBr} hrefLang="pt-BR" />
        </>
      )}
      {!omitCanonical && canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:locale" content={i18n('global.locale')} />
      <meta property="og:type" content="website" />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {!omitCanonical && ogUrl && <meta property="og:url" content={ogUrl} />}
      {imageList.length > 0 &&
        imageList.map(imgUrl => (
          <meta property="og:image" content={imgUrl} key={imgUrl} />
        ))}
      {alt && <meta property="og:image:alt" content={alt} />}
      {/* Twitter Card */}
      {title && <meta name="twitter:card" content="summary_large_image" />}
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {imageList.length > 0 && <meta name="twitter:image" content={imageList[0]} />}
      {props.children}

      <script type="application/ld+json">{JSON.stringify(ldData)}</script>
    </Helmet>
  )
}
